import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchAttendanceAdmin } from "../../actions/AttendanceAdminAction";
import homeImage from "../../assets/DTS2.png";

// ------------ Styles ------------
const PageContainer = styled(Box)(() => ({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "10px 15px",
  fontFamily: "'Poppins', sans-serif",
}));

const StickyCardsContainer = styled(Box)(() => ({
  position: "sticky",
  top: 0,
  backgroundColor: "#ffffff",
  zIndex: 100,
  paddingTop: "10px",
  paddingBottom: "10px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const ScrollableContent = styled(Box)(() => ({
  overflowY: "auto",
  maxHeight: "calc(100vh - 150px)",
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  maxHeight: "700px",
  overflowY: "auto",
  overflowX: "auto",
}));

const StyledTableCell = styled(TableCell)(() => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "6px 10px",
  fontSize: "14px",
  textAlign: "center",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "&.MuiTableCell-head": {
    backgroundColor: "#154C79",
    color: "#FFFFFF",
    fontWeight: 600,
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": { backgroundColor: "rgba(243, 246, 250, 0.6)" },
  "&:hover": { backgroundColor: "rgba(14, 60, 102, 0.09)" },
}));

export default function AttendanceAdmin() {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((state) => state.attendanceAdmin);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };

  const formatTime = (dateObj) => {
    const d = new Date(dateObj);
    return d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const sortedData = [...data].sort((a, b) => {
    if (!a.inTime && b.inTime) return 1;
    if (a.inTime && !b.inTime) return -1;
    if (!a.inTime && !b.inTime)
      return (a.designation || "").localeCompare(b.designation || "");
    const timeCompare = (a.inTime || "").localeCompare(b.inTime || "");
    if (timeCompare !== 0) return timeCompare;
    return (a.designation || "").localeCompare(b.designation || "");
  });

  useEffect(() => {
    dispatch(fetchAttendanceAdmin(formatDate(selectedDate)));
  }, [dispatch, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
  };

  const totalEmployees = data?.length || 0;
  const availableCount = data?.filter((item) => item.inTime).length || 0;
  const availablePercentage =
    totalEmployees > 0 ? ((availableCount / totalEmployees) * 100).toFixed(1) : "0.0";

  // ================= PDF EXPORT =================
  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const generatedAt = new Date();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.addImage(homeImage, "PNG", 0, 0, 210, 65);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(21, 76, 121);
    doc.text("Admin Attendance Report", pageWidth / 2, 75, { align: "center" });

    doc.setDrawColor(21, 76, 121);
    doc.setLineWidth(0.5);
    doc.line(15, 82, pageWidth - 15, 82);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(60, 60, 60);

    doc.text("Date -    ", 16, 90);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(selectedDate), 28, 90);

    doc.setFont("helvetica", "bold");
    doc.text("Time - ", pageWidth / 2 - 23, 90);
    doc.setFont("helvetica", "normal");
    doc.text(formatTime(generatedAt), pageWidth / 2 - 8, 90);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(60, 60, 60);
    doc.text("Total Employees = ", pageWidth - 67, 90);
    doc.setFont("helvetica", "normal");
    doc.text(totalEmployees.toString(), pageWidth - 25, 90);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 150, 0);
    doc.text(`  (${availablePercentage}%)`, pageWidth - 20, 90);

    autoTable(doc, {
      startY: 98,
      head: [["Name", "Designation", "In Time", "Out Time"]],
      body: sortedData.map((item) => [
        item.name || "",
        item.designation || "",
        item.inTime,
        item.outTime || (item.inTime ? "Available" : "Absent"),
      ]),
      styles: { fontSize: 12, cellPadding: 5, lineColor: [220, 220, 220], lineWidth: 0.1 },
      headStyles: { fillColor: [21, 76, 121], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 10, halign: "center", cellPadding: 6 },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      didParseCell: function (hookData) {
        if (hookData.column.index === 3 && hookData.cell.raw === "Available") {
          hookData.cell.styles.textColor = [0, 181, 26];
          hookData.cell.styles.fontStyle = "bold";
        }
        if (hookData.column.index === 3 && hookData.cell.raw === "Absent") {
          hookData.cell.styles.textColor = [211, 47, 47];
          hookData.cell.styles.fontStyle = "bold";
        }
        if (
          hookData.section === "body" &&
          hookData.column.index === 3 &&
          hookData.cell.raw !== "Available" &&
          hookData.cell.raw !== "Absent" &&
          hookData.cell.raw !== ""
        ) {
          hookData.cell.styles.textColor = [211, 47, 47];
          hookData.cell.styles.fontStyle = "bold";
        }
      },
      margin: { left: 15, right: 10 },
      theme: "grid",
      didDrawPage: function (data) {
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pageWidth - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`Admin_Attendance_${formatDate(selectedDate)}.pdf`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#0e3c66",
            textAlign: "center",
            mb: 2,
          }}
        >
          Attendance Admin
        </Typography>

        {!loading && (
          <StickyCardsContainer>
            {/* ── Date | Total | Export PDF — single row on desktop ── */}
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">

              {/* Date — half width mobile, 1/3 desktop */}
              <Grid item xs={6} sm={4}>
                <Button
                  variant="contained"
                  startIcon={<CalendarTodayIcon />}
                  onClick={() => setOpenDatePicker(true)}
                  sx={{
                    width: "100%",
                    height: "56px",
                    borderRadius: "20px",
                    backgroundColor: "#142c54",
                    fontWeight: "bold",
                    textTransform: "none",
                    fontSize: { xs: "13px", sm: "16px" },
                  }}
                >
                  {formatDate(selectedDate)}
                </Button>
              </Grid>

              {/* Total — half width mobile, 1/3 desktop */}
              <Grid item xs={6} sm={4}>
                <Box
                  sx={{
                    background: "#e8f0fa",
                    borderRadius: "20px",
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: "13px", sm: "14px" }, color: "#142c54" }}>
                    Total: {totalEmployees}&nbsp;&nbsp;
                    <Typography component="span" sx={{ fontWeight: 700, fontSize: { xs: "13px", sm: "14px" }, color: "#2e7d32" }}>
                      ({availablePercentage}%)
                    </Typography>
                  </Typography>
                </Box>
              </Grid>

              {/* Export PDF — full width mobile, 1/3 desktop */}
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportPDF}
                  disabled={loading || !data?.length}
                  sx={{
                    width: "100%",
                    height: "56px",
                    borderRadius: "20px",
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    fontWeight: "bold",
                    textTransform: "none",
                    fontSize: { xs: "13px", sm: "16px" },
                    "&:hover": { backgroundColor: "#1b5e20" },
                    "&:disabled": { backgroundColor: "#cccccc", color: "#666666" },
                  }}
                >
                  Export PDF
                </Button>
              </Grid>
            </Grid>

            {openDatePicker && (
              <DatePicker
                open={openDatePicker}
                value={selectedDate}
                onChange={handleDateChange}
                onClose={() => setOpenDatePicker(false)}
                renderInput={(params) => (
                  <TextField {...params} sx={{ display: "none" }} />
                )}
              />
            )}
          </StickyCardsContainer>
        )}

        <ScrollableContent>
          <StyledTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ textAlign: "center" }}>Name</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center" }}>InTime</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center" }}>OutTime</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center" }}>Designation</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && data?.length > 0 ? (
                  [...data]
                    .sort((a, b) => {
                      if (!a.inTime && b.inTime) return 1;
                      if (a.inTime && !b.inTime) return -1;
                      if (!a.inTime && !b.inTime)
                        return (a.designation || "").localeCompare(b.designation || "");
                      const timeCompare = (a.inTime || "").localeCompare(b.inTime || "");
                      if (timeCompare !== 0) return timeCompare;
                      return (a.designation || "").localeCompare(b.designation || "");
                    })
                    .map((item, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell sx={{ textAlign: "left" }}>{item.name}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.inTime}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>
                          {!item.inTime ? (
                            <Typography sx={{ color: "#D32F2F", fontWeight: 600, fontFamily: "'Poppins', sans-serif", fontSize: "14px" }}>
                              Absent
                            </Typography>
                          ) : item.outTime ? (
                            <Typography sx={{ color: "#D32F2F", fontWeight: 600, fontFamily: "'Poppins', sans-serif", fontSize: "14px" }}>
                              {item.outTime}
                            </Typography>
                          ) : (
                            <Typography sx={{ color: "green", fontWeight: 600, fontFamily: "'Poppins', sans-serif", fontSize: "14px" }}>
                              Available
                            </Typography>
                          )}
                        </StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "left" }}>{item.designation}</StyledTableCell>
                      </StyledTableRow>
                    ))
                ) : (
                  <TableRow>
                    <StyledTableCell colSpan={4}>
                      {loading ? "Loading..." : "No Attendance Found"}
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </ScrollableContent>
      </PageContainer>
    </LocalizationProvider>
  );
}